import { OptionType } from '@fullstory/components';
import { BySlug, getFsSpecEnabled, SpecObjectInfo } from '@fullstory/redux';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

interface SpecItem {
  slug: string;
}

export function useSelectedFsSpecSlugs<T extends SpecItem>(
  specItemsBySlug: BySlug<T>,
  initialSpecSlugs: string[] | null,
  meta: (specItem: T | undefined) => {
    active: boolean;
    displayName: string;
    group: string;
    hasMapping: boolean;
  },
  isExistingMapping: (specItem: T) => boolean,
) {
  const [selectedSpecSlugs, setSelectedSpecSlugs] = useState<OptionType<string>[] | null>(
    initialSpecSlugs
      ? initialSpecSlugs.map(slug => ({
          value: slug,
          label: meta(specItemsBySlug[slug]).displayName || slug,
          key: slug,
        }))
      : null,
  );
  const selectedSpecSlugsWithDefault = useMemo(() => selectedSpecSlugs || [], [selectedSpecSlugs]);

  const existingSpecItems = useMemo(
    () =>
      Object.values(specItemsBySlug).filter(item => meta(item).active && isExistingMapping(item)),
    [specItemsBySlug, isExistingMapping, meta],
  );

  // set fsSpecObjectSlug when specMappings are loaded if not already
  useEffect(() => {
    if (existingSpecItems.length > 0 && !selectedSpecSlugs) {
      setSelectedSpecSlugs(
        existingSpecItems.map(item => ({
          value: item.slug,
          label: meta(item).displayName || item.slug,
          key: item.slug,
        })),
      );
    }
  }, [selectedSpecSlugs, setSelectedSpecSlugs, existingSpecItems, meta]);

  const areRolesDirty = useMemo(
    () =>
      !isEqual(
        existingSpecItems?.map(item => ({
          value: item.slug,
          label: meta(item).displayName || item.slug,
          key: item.slug,
        })) || [],
        selectedSpecSlugsWithDefault,
      ),
    [selectedSpecSlugsWithDefault, existingSpecItems, meta],
  );

  const fsSpecEnabled = useSelector(getFsSpecEnabled);
  const fsSpecRoleOptions = useMemo(
    (): OptionType<string>[] =>
      Object.values(specItemsBySlug)
        .filter(item => meta(item).active || isExistingMapping(item))
        .map(item => {
          const m = meta(item);
          return {
            group: m.group,
            value: item.slug,
            label: m.displayName || item.slug,
            key: item.slug,
            // disable option if some other item is already mapped
            disabled: m.hasMapping && !isExistingMapping(item),
          };
        }),
    [specItemsBySlug, isExistingMapping, meta],
  );

  return {
    selectedSpecSlugs: selectedSpecSlugsWithDefault,
    setSelectedSpecSlugs,
    areRolesDirty,
    fsSpecEnabled,
    fsSpecRoleOptions,
    existingSpecItems,
  };
}

const specObjectInfoMeta = (specObjectInfo: SpecObjectInfo | undefined) => ({
  active: specObjectInfo?.active ?? false,
  displayName: specObjectInfo?.displayName ?? '',
  group: specObjectInfo?.spec?.displayName ?? '',
  hasMapping: !!specObjectInfo?.mapping,
});

// useSelectedFsSpecObjectSlugs is a custom hook that manages the selection of spec object / property slugs
export const useSelectedFsSpecObjectSlugs = (
  specObjectsBySlug: BySlug<SpecObjectInfo>,
  initialSpecSlugs: string[] | null,
  isExistingMapping: (specObjectInfo: SpecObjectInfo) => boolean,
) => {
  const {
    selectedSpecSlugs: selectedFsSpecObjectSlugs,
    setSelectedSpecSlugs: setSelectedFsSpecObjectSlugs,
    existingSpecItems: existingSpecObjects,
    ...rest
  } = useSelectedFsSpecSlugs<SpecObjectInfo>(
    specObjectsBySlug,
    initialSpecSlugs,
    specObjectInfoMeta,
    isExistingMapping,
  );

  return {
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    existingSpecObjects,
    ...rest,
  };
};
